import useMediaQueries from 'app/hooks/useMediaQueries';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import { store } from 'app/redux/store';
import {
  useGetProjectByIdQueryState,
  useUpdateProjectMutation,
} from 'app/services/projects';
import {
  useGetQuestionnaireByIdQueryState,
  useUpdateQuestionnaireMutation,
} from 'app/services/questionnaires';
import { IProject, Questionnaire } from 'app/services/responseTypes';
import { VerticalOptionsIcon } from 'assets/icons';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import { useCreatePopup } from 'Components/Popups';
import Spinner from 'Components/Spinner';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { StepWizardChildProps } from 'react-step-wizard';
import { isEditable } from 'utils/isEditable';
import QuestionnaireMainTabs from '../../components/QuestionnaireMainTabs';
import QuestionnaireSubTabs from '../../components/QuestionnaireSubTabs';
import { Citizenship } from '../../create/PrepareQuestionnaire/slice';
import {
  selectQuestionnaireRoutes,
  updateQuestionnaireRoutes,
} from '../routes/slice';
import AccordionLayout from './Mobile/AccordionLayout';
import { togglePreview } from './togglePreviewSlice';
import useSaveDraft from './useSaveDraft';
import { useValidateQuestionnaire } from './useValidateQuestionnaire';
export interface ITaxQuestionnaireProps {}
interface IEditTaxQuestionnaire {
  project: IProject;
  questionnaire: Questionnaire;
}
// Responsive design (adding a drop down menu)
const NoAction = () => {};
const styles = {
  saveDraftBtn: {
    lg: 'text-sm px-4 font-semibold disabled:opacity-50 bg-white text-black py-2 border-gray-100 border rounded-lg flex items-center hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 h-10',
    md: 'text-sm px-4 w-full font-semibold disabled:opacity-50 bg-white text-black flex justify-start items-center h-10',
  },
  preview: {
    lg: 'text-sm px-4 font-semibold disabled:opacity-50 bg-white text-black py-2 border-gray-100 border rounded-lg flex items-center hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 h-10',
    md: 'text-sm px-4 w-full font-semibold disabled:opacity-50 bg-white text-black flex justify-start items-center h-10',
  },
  prepareQuestionnaireBtn: {
    lg: 'flex justify-end text-sm px-4 text-green items-center border border-green rounded',
    md: 'text-sm px-4 w-full font-semibold disabled:opacity-50 bg-white text-green flex justify-start items-center h-10',
  },
};
export default function TaxQuestionnaire(
  props: ITaxQuestionnaireProps & Partial<StepWizardChildProps>,
) {
  const { questionnaireId } = useParams();
  const dispatch = useDispatch();
  const { data: questionnaireData } = useGetQuestionnaireByIdQueryState(
    questionnaireId!,
  );
  const { data: projectData } = useGetProjectByIdQueryState(questionnaireId!);
  if (!questionnaireData || !projectData) return <Spinner />;
  if (questionnaireData && projectData && isEditable(projectData?.project)) {
    return (
      <EditTaxQuestionnaire
        project={projectData.project}
        questionnaire={questionnaireData.questionnaire}
      />
    );
  }
  return (
    <Navigate
      to={`/dashboard/questionnaires/${projectData?.project.id}/preview`}
      replace={true}
    />
  );
}
export function EditTaxQuestionnaire(
  props: IEditTaxQuestionnaire & Partial<StepWizardChildProps>,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { project, questionnaire } = props;
  const projectStatus = project.status;
  const canSubmit = projectStatus === 'draft' || projectStatus === 'rejected';
  const storeQuestionnaireRoutes = useSelector(selectQuestionnaireRoutes);
  const [updateQuestionnaire, { isLoading: updateQuestionnaireIsLoading }] =
    useUpdateQuestionnaireMutation();
  const [updateProject, { isLoading: updateProjectIsLoading }] =
    useUpdateProjectMutation();
  const { saveDraft, isLoading, isSuccess } = useSaveDraft();
  const { md_down: isMobile } = useMediaQueries();
  const MobileMenuPopup = useCreatePopup();
  const validate = useValidateQuestionnaire();
  // Questionnaire Submission
  const onSubmitQuestionnaire = () => {
    let isValid = validate();
    if (isValid) {
      updateQuestionnaire({
        id: String(project.id!),
        questionnaire: {
          data: {
            prepareQuestionnaire: questionnaire?.data?.prepareQuestionnaire,
            taxQuestionnaire: store.getState().taxQuestionnaire,
          },
        },
      }).then((res) => {
        if (canSubmit) {
          updateProject({
            id: String(project.id!),
            project: {
              status_action: 'submit',
            },
          }).then((res) => {
            if ('error' in res && res?.error) {
              const error = res?.error;
              errorToast((error as Error)?.message);
            } else {
              navigate('/dashboard/questionnaires');
              successToast(
                'Submitted',
                "We'll start working on it and will respond to you shortly!",
              );
            }
          });
        } else {
          if ('error' in res && res?.error) {
            const error = res?.error;
            errorToast((error as Error)?.message);
          } else {
            successToast(
              'Submitted',
              "We'll start working on it and will respond to you shortly!",
            );
            navigate('/dashboard/questionnaires');
          }
        }
      });
    }
  };
  const saveDraftBtn = (preAction: () => void) => {
    if (projectStatus === 'draft')
      return (
        <button
          type="button"
          className={isMobile ? styles.saveDraftBtn.md : styles.saveDraftBtn.lg}
          onClick={() => {
            preAction();
            saveDraft();
          }}
          disabled={updateQuestionnaireIsLoading}
        >
          Save Draft
        </button>
      );
    return <></>;
  };
  const previewBtn = (preAction: () => void) => (
    <button
      className={isMobile ? styles.preview.md : styles.preview.lg}
      onClick={() => {
        preAction();
        dispatch(togglePreview());
      }}
    >
      Preview
    </button>
  );
  const prepareQuestionnaireBtn = () => {
    if (
      questionnaire.data.prepareQuestionnaire?.citizenship !==
      Citizenship.Not_US_Citizen_Not_GC_Holder
    )
      return (
        <Link
          to="summary/summary"
          className={
            isMobile
              ? styles.prepareQuestionnaireBtn.md
              : styles.prepareQuestionnaireBtn.lg
          }
        >
          Configure Life & Income
        </Link>
      );
    return <></>;
  };
  return (
    <>
      <DashboardContainerHeader title={project.title}>
        <div className="flex gap-4">
          <button
            onClick={onSubmitQuestionnaire}
            className="text-sm px-4 font-semibold disabled:opacity-50 bg-green text-white py-2 border-gray-100 border rounded-lg flex items-center hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 h-10"
            disabled={updateProjectIsLoading}
          >
            <span className="md-max:hidden">Submit Questionnaire</span>
            <span className="lg:hidden">Submit</span>
          </button>
          <div className="flex gap-4 md-max:hidden">
            {saveDraftBtn(NoAction)}
            {previewBtn(NoAction)}
            {prepareQuestionnaireBtn()}
          </div>
          {isMobile && (
            <>
              <div
                ref={MobileMenuPopup.setAnchor}
                className="w-10 h-full inline-grid place-items-center p-2 border-gray-light border rounded-lg"
                onClick={MobileMenuPopup.toggle}
              >
                <VerticalOptionsIcon />
              </div>
              <MobileMenuPopup.Component>
                <OverlayWindow size="" paddingSize="py-0" border="">
                  <div className="text-sm w-48">
                    {saveDraftBtn(MobileMenuPopup.close)}
                    {projectStatus === 'draft' && (
                      <hr className="border-gray-light mx-2" />
                    )}
                    {previewBtn(MobileMenuPopup.close)}
                    <hr className="border-gray-light mx-2" />
                    {prepareQuestionnaireBtn()}
                  </div>
                </OverlayWindow>
              </MobileMenuPopup.Component>
            </>
          )}
        </div>
      </DashboardContainerHeader>
      <ScrollContainer>
        <QuestionnaireMainTabs />
        {isMobile ? (
          <div
            style={{ height: '68vh' }}
            className="overflow-x-scroll no-scrollbar"
          >
            <AccordionLayout />
            <div className="hidden">
              <Outlet />
            </div>
          </div>
        ) : (
          <div className="flex h-full overflow-auto w-full">
            <ScrollContainer width="w-60 shrink-0">
              <QuestionnaireSubTabs />
            </ScrollContainer>
            <ScrollContainer className="flex-1">
              <Outlet />
            </ScrollContainer>
          </div>
        )}
      </ScrollContainer>
    </>
  );
}
